import React from 'react';

import Box from '@mui/material/Box';
import { EditorView, basicSetup } from 'codemirror';
import { Prec } from '@codemirror/state';
import { keymap } from '@codemirror/view';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter, lintGutter } from '@codemirror/lint';

export interface ICodeEditorHandle {
    /** Puts the cursor at `pos`, scrolled into view, and focuses the editor. */
    focusAt: (pos: number) => void;
}

interface ICodeEditorProps {
    value: string;
    onChange: (text: string) => void;
    onBlur?: () => void;
    /** Ctrl/Cmd+Enter */
    onApplyKey?: () => void;
    /** Ctrl/Cmd+S */
    onSaveKey?: () => void;
    error?: boolean;
}

// An empty editor is not an error here (JsonEditor treats it as "nothing yet").
const parseLinter = jsonParseLinter();
const jsonLinter = linter((view) => (view.state.doc.toString().trim() ? parseLinter(view) : []));

const theme = EditorView.theme({
    '&': { fontSize: '12px', maxHeight: '560px' },
    '&.cm-focused': { outline: 'none' },
    '.cm-scroller': { overflow: 'auto', fontFamily: 'monospace', lineHeight: 1.5 },
    '.cm-content, .cm-gutter': { minHeight: '300px' },
});

/**
 * CodeMirror 6 for JSON: line numbers, folding, search (Ctrl+F), bracket matching, undo history and
 * a parse linter. Uncontrolled inside; `value` replaces the text only when it differs, so typing
 * does not reset the cursor. The callbacks are read at call time, so they may change every render.
 */
const CodeEditor = React.forwardRef<ICodeEditorHandle, ICodeEditorProps>(({ value, error, ...callbacks }, ref) => {
    const host = React.useRef<HTMLDivElement>(null);
    const view = React.useRef<EditorView | null>(null);
    const latest = React.useRef(callbacks);
    latest.current = callbacks;

    React.useEffect(() => {
        const run = (key: 'onApplyKey' | 'onSaveKey') => () => {
            const handler = latest.current[key];
            if (!handler) return false;
            handler();
            return true;
        };
        view.current = new EditorView({
            parent: host.current!,
            doc: value,
            extensions: [
                basicSetup,
                json(),
                jsonLinter,
                lintGutter(),
                theme,
                Prec.highest(keymap.of([
                    { key: 'Mod-Enter', run: run('onApplyKey') },
                    { key: 'Mod-s', run: run('onSaveKey') },
                ])),
                // A dropped file is not inserted as text: it bubbles to the page's own drop handling.
                EditorView.domEventHandlers({
                    drop: (event) => !!event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files'),
                }),
                EditorView.updateListener.of((update) => {
                    if (update.docChanged) latest.current.onChange(update.state.doc.toString());
                    if (update.focusChanged && !update.view.hasFocus) latest.current.onBlur?.();
                }),
            ],
        });
        return () => view.current?.destroy();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const editor = view.current;
        if (!editor || editor.state.doc.toString() === value) return;
        editor.dispatch({ changes: { from: 0, to: editor.state.doc.length, insert: value } });
    }, [value]);

    React.useImperativeHandle(ref, () => ({
        focusAt: (pos: number) => {
            const editor = view.current;
            if (!editor) return;
            const anchor = Math.max(0, Math.min(pos, editor.state.doc.length));
            editor.dispatch({ selection: { anchor }, scrollIntoView: true });
            editor.focus();
        },
    }), []);

    return (
        <Box ref={host} sx={{
            border: 1, borderRadius: 1, overflow: 'hidden',
            borderColor: error ? 'error.main' : 'divider',
            '&:focus-within': { borderColor: error ? 'error.main' : 'primary.main' },
        }} />
    );
});

export default CodeEditor;
